import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { matchShortcut } from "@/lib/shortcuts";
import PatientDetectForm from "@/components/PatientDetectForm";
import { computeFields, fieldStatus, findDuplicates } from "@/lib/patientParse";
import {
  X, Search, Plus, Home, Building2, Phone as PhoneIcon, Stethoscope,
  Clock, MapPin, FileText, ChevronLeft, ChevronRight, ChevronDown, UserPlus,
} from "lucide-react";

/* ---------- helpers ---------- */
const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
const digits = (s) => (s || "").replace(/\D/g, "");
const STATUT = {
  nouveau: { label: "Nouveau", cls: "sx-badge-violet" },
  rdv_avenir: { label: "RDV à venir", cls: "sx-badge-green" },
  a_planifier: { label: "À suivre", cls: "sx-badge-blue" },
  a_relancer: { label: "À relancer", cls: "sx-badge-amber" },
};
const relDate = (iso) => {
  if (!iso) return "Jamais";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return "hier";
  if (days < 30) return `il y a ${days} j`;
  const m = Math.floor(days / 30);
  return m < 12 ? `il y a ${m} mois` : `il y a ${Math.floor(m / 12)} an(s)`;
};
const sow = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const pad = (n) => String(n).padStart(2, "0");
const hhmm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const sameDay = (a, b) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
const MONTHS_LONG = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const iconForType = (nom) => {
  const n = norm(nom);
  if (n.includes("domicile")) return Home;
  if (n.includes("telephon") || n.includes("tele")) return PhoneIcon;
  if (n.includes("cabinet")) return Building2;
  return Stethoscope;
};

const Ctx = createContext(null);
export const useCreerRdv = () => useContext(Ctx);

export function CreerRdvProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState({});
  const openCreerRdv = useCallback((opts = {}) => { setPrefill(opts || {}); setOpen(true); }, []);
  const close = useCallback(() => setOpen(false), []);

  // Raccourci clavier "N" hors d'un champ de saisie
  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented) return;
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || t?.isContentEditable) return;
      if (document.querySelector("[role='dialog']")) return;
      if (matchShortcut(e, "creer_rdv")) { e.preventDefault(); openCreerRdv(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openCreerRdv]);

  return (
    <Ctx.Provider value={{ openCreerRdv }}>
      {children}
      {open && <CreerRdvVolet prefill={prefill} onClose={close} />}
    </Ctx.Provider>
  );
}

function CreerRdvVolet({ prefill, onClose }) {
  const nav = useNavigate();
  const [patients, setPatients] = useState([]);
  const [careTypes, setCareTypes] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [hours, setHours] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [weekRdvs, setWeekRdvs] = useState([]);
  const [anchor, setAnchor] = useState(prefill.date ? new Date(prefill.date) : new Date());

  // form state
  const [patientId, setPatientId] = useState(prefill.patientId || "");
  const [pquery, setPquery] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [npText, setNpText] = useState("");
  const [npOverrides, setNpOverrides] = useState({});
  const [npSwapped, setNpSwapped] = useState(false);
  const [npEditing, setNpEditing] = useState(null);
  const [npEditVal, setNpEditVal] = useState("");
  const npInputRef = useRef(null);
  const [typeSoin, setTypeSoin] = useState("");
  const [duree, setDuree] = useState(60);
  const [slotISO, setSlotISO] = useState(prefill.date ? new Date(prefill.date).toISOString() : "");
  const [secteurId, setSecteurId] = useState("");
  const [trameSel, setTrameSel] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [note, setNote] = useState("");
  const [smsConfirm, setSmsConfirm] = useState(true);
  const [rappelVeille, setRappelVeille] = useState(true);
  const [otherDate, setOtherDate] = useState(false);
  const [pas, setPas] = useState(30);
  const [zoom, setZoom] = useState("semaine");
  const [forcedDayRdvs, setForcedDayRdvs] = useState([]);
  const [saving, setSaving] = useState(false);

  const patient = useMemo(() => patients.find((p) => p.id === patientId), [patients, patientId]);

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
    api.get("/settings/care-types").then((r) => setCareTypes(r.data)).catch(() => {});
    api.get("/settings/secteurs").then((r) => setSecteurs(r.data)).catch(() => {});
    api.get("/opening-hours").then((r) => setHours(r.data)).catch(() => {});
    api.get("/settings/soin-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      setTypeSoin((prev) => prev || r.data?.care_type_defaut || "");
      setTrameSel((prev) => prev || r.data?.trame_defaut || "");
      setPas(r.data?.pas_creneaux || 30);
    }).catch(() => {});
  }, []);

  // durée par défaut selon le type
  useEffect(() => {
    const ct = careTypes.find((c) => c.id === typeSoin);
    if (ct?.duree) setDuree(ct.duree);
  }, [typeSoin, careTypes]);

  // secteur prérempli depuis le patient
  useEffect(() => {
    if (patient?.secteurs?.length) setSecteurId((prev) => prev || patient.secteurs[0]);
  }, [patient]);

  // charge les RDV de la semaine affichée (pour créneaux libres)
  useEffect(() => {
    const s = sow(anchor); const e = new Date(s); e.setDate(e.getDate() + 7);
    api.get("/rdvs", { params: { start: s.toISOString(), end: e.toISOString() } })
      .then((r) => setWeekRdvs(r.data || [])).catch(() => {});
  }, [anchor]);

  // Échap ferme
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Focus auto sur la détection quand on ouvre « Nouveau patient ».
  useEffect(() => { if (showNew) setTimeout(() => npInputRef.current?.focus(), 30); }, [showNew]);

  // Aperçu du jour sélectionné en mode "forcer un créneau"
  useEffect(() => {
    if (!otherDate || !slotISO) return;
    const d = new Date(slotISO); const s = new Date(d); s.setHours(0, 0, 0, 0);
    const e = new Date(s); e.setDate(e.getDate() + 1);
    api.get("/rdvs", { params: { start: s.toISOString(), end: e.toISOString() } })
      .then((r) => setForcedDayRdvs(r.data || [])).catch(() => {});
  }, [otherDate, slotISO]);

  const weekDays = useMemo(() => {
    const s = sow(anchor);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(d.getDate() + i); return d; });
  }, [anchor]);

  const monthDays = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = sow(first);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  }, [anchor]);

  const zoomTitle = zoom === "mois"
    ? anchor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    : `${sow(anchor).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${(() => { const e = new Date(sow(anchor)); e.setDate(e.getDate() + 6); return e.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }); })()}`;
  const shiftZoom = (dir) => {
    const d = new Date(anchor);
    if (zoom === "semaine") d.setDate(d.getDate() + dir * 7);
    else if (zoom === "mois") d.setMonth(d.getMonth() + dir);
    else d.setFullYear(d.getFullYear() + dir);
    setAnchor(d);
  };

  const freeSlots = (day) => {
    const dow = (day.getDay() + 6) % 7;
    const oh = hours.find((h) => h.jour === dow);
    if (!oh || !oh.ouvert) return [];
    const [dh, dm] = (oh.debut || "09:00").split(":").map(Number);
    const [fh, fm] = (oh.fin || "18:00").split(":").map(Number);
    const start = new Date(day); start.setHours(dh, dm, 0, 0);
    const end = new Date(day); end.setHours(fh, fm, 0, 0);
    const now = new Date();
    const out = [];
    for (let t = new Date(start); t < end; t = new Date(t.getTime() + pas * 60000)) {
      const slotStart = new Date(t);
      const slotEnd = new Date(t.getTime() + duree * 60000);
      if (slotStart < now) continue;
      if (oh.pause) {
        const [ph, pm] = (oh.pause_debut || "12:00").split(":").map(Number);
        const [qh, qm] = (oh.pause_fin || "14:00").split(":").map(Number);
        const ps = new Date(day); ps.setHours(ph, pm, 0, 0);
        const pe = new Date(day); pe.setHours(qh, qm, 0, 0);
        if (slotStart < pe && slotEnd > ps) continue;
      }
      const busy = weekRdvs.some((r) => {
        const rs = new Date(r.date);
        const re = r.end ? new Date(r.end) : new Date(rs.getTime() + (r.duree_min || 60) * 60000);
        return slotStart < re && slotEnd > rs;
      });
      if (!busy) out.push(slotStart);
    }
    return out;
  };

  const filteredPatients = useMemo(() => {
    if (!pquery.trim()) return [];
    const nq = norm(pquery); const dq = digits(pquery);
    return patients.filter((p) => {
      const name = norm(`${p.prenom} ${p.nom}`);
      const phone = digits(p.telephone);
      return name.includes(nq) || (dq && phone.includes(dq));
    }).slice(0, 5);
  }, [patients, pquery]);

  // Détection patient réutilisable (champs + statut + doublons).
  const npFields = useMemo(() => computeFields(npText, npOverrides, npSwapped), [npText, npOverrides, npSwapped]);
  const npStatus = useMemo(() => fieldStatus(npFields), [npFields]);
  const npDups = useMemo(() => findDuplicates(patients, npFields), [patients, npFields]);
  const resetNp = () => { setNpText(""); setNpOverrides({}); setNpSwapped(false); setNpEditing(null); setNpEditVal(""); };

  const chooseSlot = (d) => { setSlotISO(d.toISOString()); setOtherDate(false); };

  const createNewPatient = async () => {
    if (!npStatus.canCreate) return;
    try {
      const { data } = await api.post("/patients", {
        prenom: npFields.prenom, nom: npFields.nom || "",
        telephone: npFields.phone ? npFields.phone.value : "",
        email: npFields.email || "",
      });
      setPatients((prev) => [data, ...prev]);
      setPatientId(data.id); setShowNew(false); setPquery(""); resetNp();
      toast.success("Patient créé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const summary = useMemo(() => {
    const parts = [];
    if (patient) parts.push(`${patient.prenom} ${patient.nom}`);
    else if (showNew && (npFields.prenom || npFields.nom)) parts.push(`${npFields.prenom} ${npFields.nom}`.trim());
    if (slotISO) {
      const s = new Date(slotISO); const e = new Date(s.getTime() + duree * 60000);
      parts.push(s.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }));
      parts.push(`${hhmm(s)} – ${hhmm(e)}`);
    }
    const ct = careTypes.find((c) => c.id === typeSoin);
    if (ct) parts.push(ct.nom);
    return parts.join(" · ");
  }, [patient, showNew, npFields, slotISO, duree, careTypes, typeSoin]);

  const canCreate = !!patientId && !!slotISO;

  const create = async () => {
    if (!canCreate) { toast.error("Sélectionnez un patient et un créneau"); return; }
    setSaving(true);
    try {
      const created = await api.post("/rdvs", {
        patient_id: patientId, secteur_id: secteurId || null,
        date: new Date(slotISO).toISOString(), duree_min: Number(duree),
        type_rdv: "soin", type_soin: typeSoin || "", montant_du: 0,
        titre: "", note, origine: "direct",
      });
      const soinId = created.data?.soin_id;
      // Trame non-défaut → appliquée à la fiche de soin créée
      const tmpl = templates.find((t) => t.id === trameSel);
      if (soinId && tmpl && !tmpl.par_defaut && tmpl.contenu_md) {
        try { await api.put(`/soins/${soinId}`, { contenu_md: tmpl.contenu_md }); } catch (_) { /* noop */ }
      }
      toast.success("RDV créé");
      window.dispatchEvent(new CustomEvent("rdv-created", { detail: { date: new Date(slotISO).toISOString() } }));
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="creer-rdv-volet">
      <div className="absolute inset-0" data-testid="creer-rdv-backdrop" onClick={onClose} />
      <div
        className="relative h-full flex flex-col bg-white sx-fade-in"
        style={{ width: 440, boxShadow: "var(--sh-float)", borderTopLeftRadius: "var(--r-float)", borderBottomLeftRadius: "var(--r-float)" }}
      >
        {/* En-tête */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
          <h2 className="font-bold tracking-[-0.01em]" style={{ fontSize: 22, color: "var(--ink)" }}>Nouveau rendez-vous</h2>
          <button data-testid="creer-rdv-close" onClick={onClose} className="sx-icon-btn" aria-label="Fermer"><X className="w-4 h-4" /></button>
        </div>

        {/* Corps défilant */}
        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-5">
          {/* a) Patient */}
          <section>
            <Label>Patient</Label>
            {patient ? (
              <div className="flex items-center justify-between rounded-lg px-3 py-2.5" style={{ background: "var(--bg-warm)" }}>
                <div className="min-w-0">
                  <div className="font-semibold text-[14px]" style={{ color: "var(--ink)" }}>{patient.prenom} {patient.nom}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="mono text-[11.5px]" style={{ color: "var(--id)" }}>{patient.id}</span>
                    <span className={`sx-badge ${(STATUT[patient.statut] || STATUT.nouveau).cls}`}>{(STATUT[patient.statut] || STATUT.nouveau).label}</span>
                  </div>
                </div>
                <button data-testid="creer-rdv-patient-change" onClick={() => { setPatientId(""); setPquery(""); }} className="text-[12px]" style={{ color: "var(--muted)" }}>Changer</button>
              </div>
            ) : (
              <div>
                {!showNew && (
                  <div className="flex items-center gap-2 sx-input" style={{ paddingLeft: 10 }}>
                    <Search className="w-[15px] h-[15px]" style={{ color: "var(--muted-2)" }} />
                    <input data-testid="creer-rdv-patient-search" autoFocus value={pquery} onChange={(e) => { setPquery(e.target.value); setShowNew(false); }}
                      placeholder="Nom ou téléphone…" className="flex-1 bg-transparent outline-none text-[13.5px]" />
                  </div>
                )}
                {(filteredPatients.length > 0 || pquery.trim()) && !showNew && (
                  <div className="mt-1.5 rounded-lg overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                    {filteredPatients.map((p) => (
                      <button key={p.id} data-testid={`creer-rdv-patient-opt-${p.id}`} onClick={() => { setPatientId(p.id); setPquery(""); }}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[var(--row-hover)]" style={{ borderBottom: "1px solid var(--divider)" }}>
                        <div className="min-w-0">
                          <div className="font-semibold text-[13px]" style={{ color: "var(--ink)" }}>{p.prenom} {p.nom}</div>
                          <div className="flex items-center gap-2">
                            <span className="mono text-[11px]" style={{ color: "var(--id)" }}>{p.id}</span>
                            <span className={`sx-badge ${(STATUT[p.statut] || STATUT.nouveau).cls}`}>{(STATUT[p.statut] || STATUT.nouveau).label}</span>
                          </div>
                        </div>
                        <span className="text-[11px]" style={{ color: "var(--muted-2)" }}>{relDate(p.dernier_contact)}</span>
                      </button>
                    ))}
                    <button data-testid="creer-rdv-patient-new" onClick={() => { setNpText(pquery.trim()); setNpOverrides({}); setNpSwapped(false); setNpEditing(null); setShowNew(true); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] font-medium hover:bg-[var(--row-hover)]" style={{ color: "var(--ink)" }}>
                      <UserPlus className="w-4 h-4" style={{ color: "var(--muted)" }} />+ Nouveau patient{pquery.trim() ? ` « ${pquery.trim()} »` : ""}
                    </button>
                  </div>
                )}
                {showNew && (
                  <div className="mt-2 space-y-2.5" data-testid="creer-rdv-new-patient">
                    <PatientDetectForm
                      text={npText} setText={setNpText}
                      overrides={npOverrides} setOverrides={setNpOverrides}
                      swapped={npSwapped} setSwapped={setNpSwapped}
                      editing={npEditing} setEditing={setNpEditing}
                      editVal={npEditVal} setEditVal={setNpEditVal}
                      fields={npFields} dups={npDups} inputRef={npInputRef}
                      idPrefix="crdv-np" showEmailChip={true} showHelp={false}
                      onEnter={() => { if (npStatus.canCreate) createNewPatient(); }}
                      dupActionLabel="Utiliser"
                      onDupAction={(p) => { setPatientId(p.id); setShowNew(false); setPquery(""); resetNp(); }}
                    />
                    {!npStatus.canCreate && npText.trim() && (
                      <div className="text-[12px]" data-testid="creer-rdv-new-missing" style={{ color: "var(--muted)" }}>{npStatus.missing}</div>
                    )}
                    <div className="flex items-center gap-2">
                      <button data-testid="creer-rdv-new-save" onClick={createNewPatient} disabled={!npStatus.canCreate} className="sx-btn-dark" style={{ height: 32, opacity: npStatus.canCreate ? 1 : 0.5 }}>Créer ce patient</button>
                      <button onClick={() => { setShowNew(false); resetNp(); }} className="text-[12px]" style={{ color: "var(--muted)" }}>Annuler</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* b) Type de soin */}
          <section>
            <Label>Type de soin</Label>
            <div className="flex flex-wrap gap-2">
              {careTypes.map((c) => {
                const Icon = iconForType(c.nom);
                const active = typeSoin === c.id;
                return (
                  <button key={c.id} data-testid={`creer-rdv-type-${c.id}`} onClick={() => setTypeSoin(c.id)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] transition-colors"
                    style={active
                      ? { background: "var(--ink)", color: "#fff" }
                      : { background: "var(--bg-warm)", color: "var(--text-2)" }}>
                    <Icon className="w-3.5 h-3.5" />{c.nom}
                  </button>
                );
              })}
              {careTypes.length === 0 && <p className="text-[12px]" style={{ color: "var(--muted-2)" }}>Aucun type. Ajoutez-en dans Paramètres › Types de soin.</p>}
            </div>
          </section>

          {/* c) Date et heure */}
          <section>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Label className="mb-0">Date et heure</Label>
                {!otherDate && zoom !== "annee" && (
                  <button type="button" data-testid="creer-rdv-zoom-out" onClick={() => setZoom(zoom === "semaine" ? "mois" : "annee")}
                    className="text-[12px] font-semibold rounded px-1.5 py-0.5 capitalize hover:bg-[var(--bg-warm)]" style={{ color: "var(--ink)" }}>{zoomTitle} ▾</button>
                )}
                {!otherDate && zoom === "annee" && <span className="text-[12px] font-semibold mono" style={{ color: "var(--ink)" }}>{anchor.getFullYear()}</span>}
              </div>
              {!otherDate && (
                <div className="flex items-center gap-1">
                  <button data-testid="creer-rdv-week-prev" onClick={() => shiftZoom(-1)} className="sx-icon-btn" aria-label="Précédent"><ChevronLeft className="w-4 h-4" /></button>
                  <button data-testid="creer-rdv-week-next" onClick={() => shiftZoom(1)} className="sx-icon-btn" aria-label="Suivant"><ChevronRight className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            {!otherDate ? (
              <>
                {zoom === "mois" && (
                  <div className="grid grid-cols-7 gap-1" data-testid="creer-rdv-month">
                    {["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"].map((l, li) => <div key={li} className="text-center text-[10px]" style={{ color: "var(--muted-2)" }}>{l}</div>)}
                    {monthDays.map((d, i) => { const inM = d.getMonth() === anchor.getMonth(); const today = sameDay(d, new Date()); return (
                      <button key={i} data-testid={`creer-rdv-month-day-${d.toISOString().slice(0, 10)}`} onClick={() => { setAnchor(new Date(d)); setZoom("semaine"); }}
                        className="aspect-square rounded-md text-[12px] flex items-center justify-center transition-colors" style={{ color: inM ? "var(--ink)" : "var(--faint)", background: today ? "var(--accent)" : "var(--bg-warm)", fontWeight: today ? 700 : 500 }}>{d.getDate()}</button>); })}
                  </div>
                )}
                {zoom === "annee" && (
                  <div className="grid grid-cols-3 gap-2" data-testid="creer-rdv-year">
                    {MONTHS_LONG.map((m, i) => { const cur = new Date().getMonth() === i && new Date().getFullYear() === anchor.getFullYear(); return (
                      <button key={i} data-testid={`creer-rdv-year-month-${i}`} onClick={() => { setAnchor(new Date(anchor.getFullYear(), i, 1)); setZoom("mois"); }}
                        className="py-3 rounded-lg text-[12.5px] capitalize transition-opacity hover:opacity-80" style={{ background: "var(--bg-warm)", color: "var(--ink)", fontWeight: cur ? 700 : 500 }}>{m}</button>); })}
                  </div>
                )}
                {zoom === "semaine" && (<>
                <div className="overflow-x-auto pb-1">
                <div className="grid grid-cols-7 gap-1" style={{ minWidth: 520 }}>
                  {weekDays.map((d, i) => {
                    const slots = freeSlots(d);
                    return (
                      <div key={i} className="min-w-0">
                        <div className="text-center text-[10.5px] mb-1" style={{ color: "var(--muted-2)" }}>
                          {["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"][i]}<span className="mono block">{d.getDate()}</span>
                        </div>
                        <div className="space-y-1">
                          {slots.length === 0 ? <div className="text-center text-[10px]" style={{ color: "var(--faint)" }}>—</div>
                            : slots.map((s) => {
                              const sel = slotISO && new Date(slotISO).getTime() === s.getTime();
                              return (
                                <button key={s.toISOString()} data-testid={`creer-rdv-slot-${s.toISOString()}`} onClick={() => chooseSlot(s)}
                                  className="w-full mono text-[10.5px] rounded py-1 transition-colors"
                                  style={sel ? { background: "var(--accent)", color: "var(--ink)", fontWeight: 600 } : { background: "var(--bg-warm)", color: "var(--text-2)" }}>
                                  {hhmm(s)}
                                </button>
                              );
                            })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
                </>)}
                <button data-testid="creer-rdv-other-date" onClick={() => setOtherDate(true)} className="mt-2 text-[12px]" style={{ color: "oklch(0.42 0.11 255)" }}>Autre date · forcer un créneau</button>
              </>
            ) : (
              <div>
                <div className="flex items-center gap-2">
                  <input data-testid="creer-rdv-datetime" type="datetime-local" value={slotISO ? toLocalInput(slotISO) : ""} onChange={(e) => setSlotISO(e.target.value ? new Date(e.target.value).toISOString() : "")} className="sx-input" style={{ fontSize: 13 }} />
                  <button onClick={() => setOtherDate(false)} className="text-[12px]" style={{ color: "var(--muted)" }}>Retour</button>
                </div>
                <p className="mt-1.5 text-[11.5px]" style={{ color: "var(--muted-2)" }}>Saisie manuelle : vous pouvez forcer un rendez-vous, même sur un créneau fermé ou hors horaires (urgence).</p>
                {slotISO && (
                  <div className="mt-2 rounded-lg p-2.5" style={{ background: "var(--bg-warm)" }} data-testid="creer-rdv-day-preview">
                    <div className="text-[11px] font-semibold mb-1 capitalize" style={{ color: "var(--muted-2)" }}>{new Date(slotISO).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
                    {forcedDayRdvs.length === 0
                      ? <div className="text-[11.5px]" style={{ color: "var(--faint)" }}>Aucun RDV ce jour-là</div>
                      : [...forcedDayRdvs].sort((a, b) => (a.date < b.date ? -1 : 1)).map((r) => { const st = new Date(r.date); const en = r.end ? new Date(r.end) : new Date(st.getTime() + (r.duree_min || 60) * 60000); const isAbs = r.type_rdv === "absence"; const overlap = new Date(slotISO) < en && new Date(new Date(slotISO).getTime() + duree * 60000) > st; return (
                        <div key={r.id} className="flex items-center gap-2 text-[11.5px] py-0.5" style={{ opacity: isAbs ? 0.7 : 1 }}>
                          <span className="mono shrink-0" style={{ color: overlap ? "oklch(0.6 0.16 25)" : "var(--text-2)" }}>{hhmm(st)}–{hhmm(en)}</span>
                          <span className="truncate" style={{ color: isAbs ? "var(--muted-2)" : "var(--ink)" }}>{isAbs ? (r.titre || "Fermé") : (r.patient_nom || r.titre || "RDV")}</span>
                        </div>); })}
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 mt-2">
              <span className="sx-info" style={{ padding: "5px 9px" }}><Clock className="w-3.5 h-3.5" />
                <input data-testid="creer-rdv-duree" type="number" value={duree} onChange={(e) => setDuree(e.target.value)} className="w-12 bg-transparent outline-none mono text-[12px]" /> min
              </span>
            </div>
          </section>

          {/* d) Secteur */}
          <section>
            <Label>Secteur</Label>
            <div className="flex items-center gap-2 sx-input">
              <MapPin className="w-4 h-4" style={{ color: "var(--muted-2)" }} />
              <select data-testid="creer-rdv-secteur" value={secteurId} onChange={(e) => setSecteurId(e.target.value)} className="flex-1 bg-transparent outline-none text-[13px]">
                <option value="">Secteur à renseigner</option>
                {secteurs.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
              </select>
            </div>
          </section>

          {/* e) Trame */}
          <section>
            <Label>Trame</Label>
            <div className="inline-flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: "var(--ink)", color: "#fff" }}>
              <FileText className="w-3.5 h-3.5" style={{ color: "var(--accent)" }} />
              <span className="text-[12.5px]">Trame :</span>
              <select data-testid="creer-rdv-trame" value={trameSel} onChange={(e) => setTrameSel(e.target.value)} className="bg-transparent outline-none text-[12.5px]" style={{ color: "#fff" }}>
                <option value="" style={{ color: "var(--ink)" }}>Aucune / défaut</option>
                {templates.map((t) => <option key={t.id} value={t.id} style={{ color: "var(--ink)" }}>{t.nom}</option>)}
              </select>
            </div>
          </section>

          {/* f) Plus d'options */}
          <section>
            <button data-testid="creer-rdv-more" onClick={() => setShowOptions((o) => !o)} className="inline-flex items-center gap-1 text-[12.5px]" style={{ color: "var(--muted)" }}>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showOptions ? "rotate-180" : ""}`} />Plus d'options
            </button>
            {showOptions && (
              <div className="mt-2 space-y-2">
                <textarea data-testid="creer-rdv-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note interne…" className="w-full rounded-lg px-3 py-2 text-[13px] resize-none" style={{ border: "1px solid var(--border-input)" }} />
                <label className="flex items-center gap-2 text-[12.5px]" style={{ color: "var(--text-2)" }}>
                  <input data-testid="creer-rdv-sms" type="checkbox" checked={smsConfirm} onChange={(e) => setSmsConfirm(e.target.checked)} disabled={!patient?.telephone} />SMS de confirmation
                </label>
                <label className="flex items-center gap-2 text-[12.5px]" style={{ color: "var(--text-2)" }}>
                  <input data-testid="creer-rdv-rappel" type="checkbox" checked={rappelVeille} onChange={(e) => setRappelVeille(e.target.checked)} />Rappel la veille
                </label>
              </div>
            )}
          </section>
        </div>

        {/* Pied fixe */}
        <div className="shrink-0 px-5 py-4 space-y-2.5" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="text-[13px] min-h-[18px]" style={{ color: "var(--muted)" }} data-testid="creer-rdv-summary">{summary || "Complétez les informations…"}</div>
          <button data-testid="creer-rdv-submit" onClick={create} disabled={!canCreate || saving} className="sx-btn-dark w-full justify-center" style={{ height: 42, opacity: canCreate && !saving ? 1 : 0.5 }}>
            {saving ? "Création…" : "Créer le rendez-vous"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Label = ({ children, className = "" }) => (
  <div className={`text-[11px] font-semibold uppercase tracking-wider mb-1.5 ${className}`} style={{ color: "var(--muted-2)", letterSpacing: ".06em" }}>{children}</div>
);

function toLocalInput(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
